/**
 * FINDINGS-DOMAIN-1 Session 4, Surface 2 — the customer quote.
 *
 * Intake facts -> recommended tier -> scope + anchor price + turnaround ->
 * optional human discount -> accept. Every figure is computed server-side by
 * POST /api/qoe/quote so the quote, the report scope and the invoice cannot
 * disagree; this file does no arithmetic on fees at all.
 *
 * "Accept" hands off to /engagements/new rather than creating the deal here.
 * Deal creation already owns conflict checks, token issuance and the
 * tier-override audit trail — a second creation path would be a second copy of
 * those rules to keep in step. The accepted quote is passed in router state and
 * mirrored to sessionStorage; EngagementCreate.tsx does not read it yet (522
 * lines, over the FILE_SIZE_POLICY split-first band), so that prefill is a
 * Session 5 item and the button here promises nothing more than navigation.
 *
 * Fee figures obey PRICING_ENABLED like every other "what Yansar charges"
 * surface. With the flag off the page still answers the scope question — what
 * is assessed, at which depth, in how long — and simply omits the dollars.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { buildQuote, fmtFee, type QuoteResponse } from '@/api/scope'
import { PRICING_ENABLED } from '@/lib/pricing'
import { VERTICALS, OTHER_VERTICAL } from '@/lib/verticals'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatApiError } from '@/utils/httpErrors'

export function QuoteBuilder() {
  const navigate = useNavigate()

  const [ebitda, setEbitda] = useState('')
  const [dealSide, setDealSide] = useState('buy')
  const [entityCount, setEntityCount] = useState('1')
  const [industry, setIndustry] = useState(OTHER_VERTICAL)
  const [taxDiligence, setTaxDiligence] = useState(false)
  const [tier, setTier] = useState<string | null>(null)
  const [discountPct, setDiscountPct] = useState('')
  const [discountReason, setDiscountReason] = useState('')

  const [quote, setQuote] = useState<QuoteResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ebitdaValue = useMemo(() => {
    const n = Number(String(ebitda).replace(/[$,\s]/g, ''))
    return Number.isFinite(n) && n >= 0 ? n : null
  }, [ebitda])

  // A discount is a fee concession, so it does not exist when fees are hidden.
  const discountValue = useMemo(() => {
    if (!PRICING_ENABLED) return 0
    const n = Number(discountPct)
    return Number.isFinite(n) && n > 0 ? n : 0
  }, [discountPct])

  const canQuote = ebitdaValue !== null && ebitda.trim() !== ''
  // Mirrors the server's own 422. Enforced there too — this is only so the
  // button explains itself rather than failing after a round trip.
  const discountNeedsReason = discountValue > 0 && discountReason.trim() === ''

  async function requestQuote(overrideTier?: string | null) {
    if (ebitdaValue === null) return
    setLoading(true)
    setError(null)
    try {
      const result = await buildQuote({
        ebitda: ebitdaValue,
        tier: overrideTier ?? tier ?? undefined,
        deal_side: dealSide,
        entity_count: Number(entityCount) || undefined,
        industry: industry || undefined,
        tax_diligence: taxDiligence,
        discount_pct: discountValue || undefined,
        discount_reason: discountReason.trim() || undefined,
      })
      setQuote(result)
      setTier(result.tier)
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  // Re-price when the tier is switched on an existing quote. Not debounced on
  // EBITDA: the discount is audit-logged on every call, and a log entry per
  // keystroke would bury the concessions that matter.
  useEffect(() => {
    if (quote && tier && tier !== quote.tier) void requestQuote(tier)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier])

  function accept() {
    if (!quote) return
    const accepted = {
      scope_tier: quote.tier,
      base_fee: quote.base_fee,
      ebitda_estimate: quote.ebitda,
      vertical: industry,
      engagement_type: dealSide === 'sell' ? 'sell_side' : 'buy_side',
      discount_pct: quote.discount_pct,
      discount_reason: quote.discount_reason,
    }
    // sessionStorage as well as router state: the intake form is a separate
    // page that a user may reload, and a quote that vanishes on refresh would
    // be re-priced by hand.
    try {
      sessionStorage.setItem('qoe.accepted_quote', JSON.stringify(accepted))
    } catch {
      /* private-mode storage failure must not block the hand-off */
    }
    navigate('/engagements/new', { state: { quote: accepted } })
  }

  const industries = useMemo(
    () => (VERTICALS.includes(OTHER_VERTICAL) ? [...VERTICALS] : [...VERTICALS, OTHER_VERTICAL]),
    [],
  )

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl">
      <header>
        <h1 className="text-2xl font-semibold">Quality of Earnings — quote</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tell us about the business and we will show exactly what is assessed, what it
          costs and how long it takes.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">About the engagement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="ebitda">Annual EBITDA (USD)</Label>
              <Input
                id="ebitda"
                inputMode="decimal"
                placeholder="2,500,000"
                value={ebitda}
                onChange={e => setEbitda(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used to recommend a service level and to price it. An estimate is fine.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="deal-side">Which side are you on?</Label>
                <select
                  id="deal-side"
                  className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                  value={dealSide}
                  onChange={e => setDealSide(e.target.value)}
                >
                  <option value="buy">Buying a business</option>
                  <option value="sell">Selling a business</option>
                </select>
              </div>
              <div>
                <Label htmlFor="entities">Number of legal entities</Label>
                <Input
                  id="entities"
                  inputMode="numeric"
                  value={entityCount}
                  onChange={e => setEntityCount(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="industry">Industry</Label>
              <select
                id="industry"
                className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                value={industry}
                onChange={e => setIndustry(e.target.value)}
              >
                {industries.map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={taxDiligence}
                onChange={e => setTaxDiligence(e.target.checked)}
              />
              Tax due diligence also required
            </label>

            {PRICING_ENABLED && (
              <div className="pt-2 border-t space-y-3">
                <div>
                  <Label htmlFor="discount">Discount (%) — CPA or referral partner</Label>
                  <Input
                    id="discount"
                    inputMode="decimal"
                    placeholder="0"
                    value={discountPct}
                    onChange={e => setDiscountPct(e.target.value)}
                  />
                </div>
                {discountValue > 0 && (
                  <div>
                    <Label htmlFor="discount-reason">Reason for the discount</Label>
                    <Textarea
                      id="discount-reason"
                      rows={2}
                      value={discountReason}
                      onChange={e => setDiscountReason(e.target.value)}
                      placeholder="Referral partner rate, repeat client, …"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Required. Recorded in the audit trail with the anchor and quoted fee.
                    </p>
                  </div>
                )}
              </div>
            )}

            <Button
              onClick={() => void requestQuote()}
              disabled={!canQuote || discountNeedsReason || loading}
              className="w-full"
            >
              {loading ? 'Pricing…' : quote ? 'Update quote' : 'Get my quote'}
            </Button>
            {discountNeedsReason && (
              <p className="text-xs text-destructive">
                Enter a reason before applying a discount.
              </p>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Your quote</CardTitle>
          </CardHeader>
          <CardContent>
            {!quote ? (
              <p className="text-sm text-muted-foreground">
                Enter an EBITDA figure to see scope and pricing.
              </p>
            ) : (
              <div className="space-y-5">
                <div>
                  {PRICING_ENABLED ? (
                    <>
                      <div className="text-3xl font-semibold tabular-nums">
                        {fmtFee(quote.base_fee)}
                      </div>
                      {quote.discount_pct > 0 && (
                        <div className="text-sm text-muted-foreground mt-1">
                          <span className="line-through">{fmtFee(quote.anchor_fee)}</span>
                          {' '}· {quote.discount_pct}% discount applied
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-2xl font-semibold">{quote.tier_label}</div>
                  )}
                  <div className="text-sm text-muted-foreground mt-1">
                    {PRICING_ENABLED ? `${quote.tier_label} · ${quote.turnaround}` : quote.turnaround}
                  </div>
                </div>

                <div className="rounded-md bg-muted/50 p-3 text-sm">
                  We assess <strong>{quote.in_scope_count} of {quote.catalogue_count}</strong>{' '}
                  diligence procedures across the {quote.section_count} risk areas.
                </div>

                <div>
                  <div className="text-sm font-medium mb-2">Service level</div>
                  <div className="space-y-2">
                    {quote.tiers.map(t => (
                      <label
                        key={t.key}
                        className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer ${
                          t.key === quote.tier ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <input
                          type="radio"
                          name="tier"
                          className="mt-1"
                          checked={t.key === quote.tier}
                          onChange={() => setTier(t.key)}
                        />
                        <span className="min-w-0">
                          <span className="font-medium">{t.label}</span>
                          {t.key === quote.recommended_tier && (
                            <span className="ml-2 text-xs rounded bg-primary/10 text-primary px-1.5 py-0.5">
                              Recommended
                            </span>
                          )}
                          <span className="block text-xs text-muted-foreground mt-0.5">
                            {t.finding_count} of {quote.catalogue_count} procedures
                            {t.turnaround ? ` · ${t.turnaround}` : ''}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium mb-2">Coverage by risk area</div>
                  <ul className="space-y-1">
                    {quote.sections.map(s => (
                      <li key={s.letter} className="flex items-center gap-2 text-sm">
                        <span className="flex-1 min-w-0 truncate">
                          {s.letter}. {s.label}
                        </span>
                        <span className="tabular-nums text-muted-foreground shrink-0">
                          {s.in_scope}/{s.finding_count}
                        </span>
                        <span className="w-16 h-1.5 rounded bg-muted overflow-hidden shrink-0">
                          <span
                            className="block h-full bg-primary"
                            style={{
                              width: `${s.finding_count ? (s.in_scope / s.finding_count) * 100 : 0}%`,
                            }}
                          />
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button className="w-full" onClick={accept}>
                  Accept and set up the engagement
                </Button>
                <p className="text-xs text-muted-foreground">
                  {PRICING_ENABLED
                    ? 'Indicative fee. Final scope and fee are confirmed in the engagement letter.'
                    : 'Indicative scope. Final scope is confirmed in the engagement letter.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default QuoteBuilder
